import React, { useState, useRef, useEffect } from 'react';
import { Conversation } from '../types';
import EllipsisVertical from '../../../macadamia-core/icons/EllipsisVertical';
import ChevronLeft from '../../../macadamia-core/icons/ChevronLeft';
import ChevronRight from '../../../macadamia-core/icons/ChevronRight';

interface DamiaSidebarProps {
    conversations: Conversation[];
    currentConversationId: string | null;
    onSelectConversation: (id: string) => void;
    onNewConversation: () => void;
    isCollapsed: boolean;
    onToggleSidebar: () => void;
    onRenameConversation?: (id: string, newTitle: string) => void;
    onArchiveConversation?: (id: string) => void;
    typingTitle?: { conversationId: string; title: string } | null;
    onTypingComplete?: () => void;
}

const DamiaSidebar: React.FC<DamiaSidebarProps> = ({
    conversations,
    currentConversationId,
    onSelectConversation,
    onNewConversation,
    isCollapsed,
    onToggleSidebar,
    onRenameConversation,
    onArchiveConversation,
    typingTitle,
    onTypingComplete
}) => {
    const [hoveredConversationId, setHoveredConversationId] = useState<string | null>(null);
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [renamingId, setRenamingId] = useState<string | null>(null);
    const [renameTitle, setRenameTitle] = useState('');
    const [displayedTypingTitle, setDisplayedTypingTitle] = useState('');
    const [typingConversationId, setTypingConversationId] = useState<string | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const renameInputRef = useRef<HTMLInputElement>(null);

    // Fermer le menu quand on clique à l'extérieur
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setOpenMenuId(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Focus sur l'input de renommage quand il apparaît
    useEffect(() => {
        if (renamingId && renameInputRef.current) {
            renameInputRef.current.focus();
        }
    }, [renamingId]);

    // Effet de frappe pour le nouveau titre
    useEffect(() => {
        if (typingTitle) {
            setTypingConversationId(typingTitle.conversationId);
            setDisplayedTypingTitle('');

            let index = 0;
            const interval = setInterval(() => {
                if (index < typingTitle.title.length) {
                    setDisplayedTypingTitle(typingTitle.title.slice(0, index + 1));
                    index++;
                } else {
                    clearInterval(interval);
                    // Petit délai avant de terminer pour que le dernier caractère soit visible
                    setTimeout(() => {
                        setTypingConversationId(null);
                        setDisplayedTypingTitle('');
                        onTypingComplete?.();
                    }, 200);
                }
            }, 35); // 35ms par caractère

            return () => clearInterval(interval);
        }
    }, [typingTitle, onTypingComplete]);

    const handleRenameClick = (conversationId: string, currentTitle: string) => {
        setRenamingId(conversationId);
        setRenameTitle(currentTitle);
        setOpenMenuId(null);
    };

    const handleRenameSubmit = (conversationId: string) => {
        if (renameTitle.trim() && onRenameConversation) {
            onRenameConversation(conversationId, renameTitle.trim());
        }
        setRenamingId(null);
        setRenameTitle('');
    };

    const handleArchiveClick = (conversationId: string) => {
        if (onArchiveConversation) {
            onArchiveConversation(conversationId);
        }
        setOpenMenuId(null);
    };

    return (
        <div
            className={`h-full bg-mcdm-primary-200 dark:bg-mcdm-third-400 border-r 
                border-gray-200 dark:border-gray-700 transition-all duration-300 flex flex-col
                ${isCollapsed ? 'w-16' : 'w-72'}`}
        >
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                {!isCollapsed && (
                    <h2 className="text-lg font-platypi text-mcdm-third-500 dark:text-mcdm-primary-100">
                        Conversations
                    </h2>
                )}
                <button
                    onClick={onToggleSidebar}
                    className="p-2 rounded-md text-mcdm-third-400 dark:text-mcdm-primary-300 
                   hover:bg-gray-100 dark:hover:bg-mcdm-third-400"
                >
                    {isCollapsed ? (
                        <ChevronRight fill="currentColor" width="20px" height="20px" />
                    ) : (
                        <ChevronLeft fill="currentColor" width="20px" height="20px" />
                    )}
                </button>
            </div>

            <div className="p-2">
                <button
                    onClick={onNewConversation}
                    className={`w-full mb-2 flex items-center justify-center bg-mcdm-green-400
                    hover:bg-mcdm-green-500 text-white rounded-md transition-colors
                    duration-200 p-2 ${isCollapsed ? 'px-2' : 'px-4'}`}
                >
                    {isCollapsed ? "+" : "Nouvelle conversation"}
                </button>
            </div>

            <div className="flex-1 overflow-y-auto overflow-x-visible relative">
                {!isCollapsed && conversations.map((conversation) => (
                    <div
                        key={conversation.id}
                        onMouseEnter={() => setHoveredConversationId(conversation.id)}
                        onMouseLeave={() => setHoveredConversationId(null)}
                        className={`relative px-4 py-3 border-b border-gray-200 dark:border-gray-700 cursor-pointer
                      hover:bg-gray-100 dark:hover:bg-mcdm-third-300 transition-colors duration-200
                      ${currentConversationId === conversation.id
                                ? 'bg-gray-200 dark:bg-mcdm-third-300'
                                : ''}`}
                    >
                        <div onClick={() => onSelectConversation(conversation.id)}>
                            {renamingId === conversation.id ? (
                                <input
                                    ref={renameInputRef}
                                    type="text"
                                    value={renameTitle}
                                    onChange={(e) => setRenameTitle(e.target.value)}
                                    onBlur={() => handleRenameSubmit(conversation.id)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleRenameSubmit(conversation.id);
                                        } else if (e.key === 'Escape') {
                                            setRenamingId(null);
                                        }
                                    }}
                                    className="w-full px-2 py-1 text-sm bg-white dark:bg-mcdm-third-500 
                                             text-mcdm-third-500 dark:text-mcdm-primary-100 
                                             border border-gray-300 dark:border-gray-600 rounded"
                                    onClick={(e) => e.stopPropagation()}
                                />
                            ) : (
                                <div className="text-sm font-medium text-mcdm-third-500 dark:text-mcdm-primary-100 truncate pr-8">
                                    {typingConversationId === conversation.id ? (
                                        <span>
                                            {displayedTypingTitle}
                                            <span className="animate-pulse">|</span>
                                        </span>
                                    ) : (
                                        conversation.title
                                    )}
                                </div>
                            )}
                            {!renamingId && (
                                <div className="text-xs text-mcdm-third-400 dark:text-mcdm-primary-300 mt-1">
                                    {conversation.lastMessageDate.toLocaleDateString()}
                                </div>
                            )}
                        </div>

                        {/* Menu actions avec z-index plus élevé et positionnement amélioré */}
                        {(hoveredConversationId === conversation.id || openMenuId === conversation.id) && 
                         !renamingId && (
                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 z-50">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenMenuId(openMenuId === conversation.id ? null : conversation.id);
                                    }}
                                    className="p-1 hover:bg-gray-200 dark:hover:bg-mcdm-third-500 rounded"
                                >
                                    <EllipsisVertical className="w-4 h-4 text-mcdm-third-400 dark:text-mcdm-primary-300" />
                                </button>

                                {openMenuId === conversation.id && (
                                    <div
                                        ref={menuRef}
                                        className="absolute right-0 mt-1 w-36 bg-white dark:bg-mcdm-third-500 
                                                 rounded-md shadow-lg z-50 border border-gray-200 dark:border-gray-700
                                                 overflow-visible"
                                        style={{
                                            position: 'absolute',
                                            zIndex: 50,
                                            right: '0px',
                                        }}
                                    >
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRenameClick(conversation.id, conversation.title);
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm text-mcdm-third-500 
                                                     dark:text-mcdm-primary-100 hover:bg-gray-100 
                                                     dark:hover:bg-mcdm-third-400 rounded-t-md"
                                        >
                                            Renommer
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleArchiveClick(conversation.id);
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm text-red-600 
                                                     dark:text-red-400 hover:bg-gray-100 
                                                     dark:hover:bg-mcdm-third-400 rounded-b-md"
                                        >
                                            Archiver
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}

                {isCollapsed && conversations.map((conversation) => (
                    <div
                        key={conversation.id}
                        onClick={() => onSelectConversation(conversation.id)}
                        className={`p-2 mb-1 flex justify-center cursor-pointer rounded-md
                      hover:bg-gray-100 dark:hover:bg-mcdm-third-400 transition-colors duration-200
                      ${currentConversationId === conversation.id
                                ? 'bg-gray-200 dark:bg-mcdm-third-400'
                                : ''}`}
                    >
                        <div className="w-8 h-8 bg-mcdm-secondary-400 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs">
                                {conversation.title.charAt(0).toUpperCase()}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default DamiaSidebar;
